import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setSection, updateSection } from "../../../assignmentSlice";

import { parseScrambleQuestion } from "../../../../../utils/parseUtils";
import { getStartingIndex } from "../../../../../utils/resumeUtils";

import ScrambleButton from "../../widgets/ScrambleButton";
import { typeInfo } from "../../../../../constants/typeInfo";
import CommonPage from "../../common/CommonPage";
import ComprehensiveBookHead from "../ComprehensiveBookHead";

const ScramblePage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section } = useSelector((state) => state.assignment);

  const [pageList, setPageList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [wordList, setWordList] = useState([]);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const currentPage = pageList[currentIndex] || {
    type: "",
  };

  useEffect(() => {
    if (currentPage.scramble) {
      setWordList(
        currentPage.scramble
          .filter((element) => element.type === "scramble")
          .map((element) => element.content)[0]
      );
    }
  }, [currentIndex]);

  useEffect(() => {
    if (pageList.length > 0) return;

    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }
    setPageList(
      section.question.data.map((question) => {
        const scramble = parseScrambleQuestion(question.scramble);
        let answer = question.answer;
        let scrambleContent;
        for (let i = 0; i < scramble.length; i++) {
          if (scramble[i].type === "text") {
            answer = answer.replace(scramble[i].content, "");
          } else if (scramble[i].type === "scramble") {
            scrambleContent = scramble[i].content;
          }
        }
        answer = answer.split(" ").map((word) => {
          return {
            text: word,
            index: scrambleContent.find((content) => content.text === word)
              .index,
          };
        });
        return {
          ...question,
          scramble: scramble,
          answer: answer,
          input: [],
          isSubmitted: false,
          isCorrect: false,
        };
      })
    );
    setCurrentIndex(0);
  }, [section]);

  const handleClickWord = (word) => {
    const prevInput = currentPage.input;
    const currentInput = prevInput.some((input) => input.index === word.index)
      ? prevInput.filter((input) => input.index !== word.index)
      : [...prevInput, word];

    const newPageList = pageList.map((prevPage, index) => {
      if (index === currentIndex) {
        return {
          ...prevPage,
          scramble: prevPage.scramble.map((element) => {
            if (element.type === "scramble") {
              return {
                ...element,
              };
            }
            return element;
          }),
          input: currentInput,
        };
      }
      return prevPage;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
  };

  const handleNextPage = () => {
    if (currentIndex < pageList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      let isAllAnswered = true;

      pageList.forEach((page, index) => {
        if (!page.isSubmitted) {
          isAllAnswered = false;
        }
      });

      if (isAllAnswered) {
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: { is_finished: true, answer: pageList },
            })
          );
        }
        alert("학습이 완료되었습니다.");
        setCurrentStep(2);
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
  };

  const handlePreviousPage = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = () => {
    if (currentPage.isSubmitted) return;
    if (currentPage.input.length < currentPage.answer.length) {
      alert("빈칸을 전부 채워주세요.");
      return;
    }
    const correct = currentPage.answer.every((answer, index) => {
      return answer.text === currentPage.input[index].text;
    });
    const newPageList = pageList.map((page, index) => {
      if (index === currentIndex) {
        return {
          ...page,
          isSubmitted: true,
          isCorrect: correct,
        };
      }
      return page;
    });

    setOverlayVisible(true);
    setIsCorrect(correct);
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));

    setTimeout(() => {
      setOverlayVisible(false);
    }, 600);
  };

  return (
    <CommonPage
      currentIndex={currentIndex}
      handleNextPage={handleNextPage}
      handlePreviousPage={handlePreviousPage}
      currentPage={currentPage}
      totalNo={pageList.length}
      paragraph={section?.question.full_text}
      accentColor={typeInfo.comprehensive.color}
      overlayVisible={overlayVisible}
      isCorrect={isCorrect}
      handleSubmit={handleSubmit}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "20px",
          width: Math.min(window.innerWidth, window.screen.width) <= 768 ? `${Math.min(window.innerWidth, window.screen.width) - 56}px` : "90%", // [여백 수술] 화면 기준 폭
          maxWidth: "680px",
          margin: "0 auto", // [중앙 정렬] 지문+문제 열이 왼쪽에 붙던 것 — 남는 폭을 좌우로 균등 분배
          boxSizing: "border-box",
          marginBottom: "60px",
        }}
      >
        <ComprehensiveBookHead index={currentIndex} total={pageList.length} instruction="위 문장과 의미가 비슷하도록, 아래 문장을 완성하세요." />
        <div className="comp-book__question" style={{ fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "19px" : "28px", color: "#2D2D2D" }}>
          {currentPage.question}
        </div>
        <div
          style={{
            fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "19px" : "28px", // [타이포 통일] 문장테스트와 동일
            marginBottom: "10px",
          }}
        >
          {currentPage.scramble && (
            <div style={{ wordBreak: "break-word", display: "inline" }}>
              {currentPage.scramble
                .map((element) => {
                  if (element.type === "scramble") {
                    const blankLength = element.length;
                    const userAnswer = currentPage.isSubmitted
                      ? currentPage.answer
                      : currentPage.input || [];

                    return userAnswer
                      .map((answer) => answer.text)
                      .join(" ")
                      .concat(" ")
                      .padEnd(blankLength, "_");
                  } else {
                    return element.content;
                  }
                })
                .join(" ")}
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "20px",
          }}
        >
          {wordList.length > 0 && (
            <ScrambleButton
              wordList={wordList.slice()}
              currentPage={currentPage}
              handleClickWord={handleClickWord}
              type={"comprehensive"}
            />
          )}
        </div>
      </div>
    </CommonPage>
  );
};

export default ScramblePage;
